import { formatDate } from '@angular/common';
import { ChangeDetectionStrategy, Component, computed, effect, input, output, signal, viewChild } from '@angular/core';
import { FormsModule, NgModel } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { DateAdapter, MAT_DATE_LOCALE } from '@angular/material/core';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatProgressBarModule } from '@angular/material/progress-bar';
import { MatTooltipModule } from '@angular/material/tooltip';
import { PolishDateAdapter } from '@/common/polish-date-adapter/polish-date-adapter';
import { MailListValidatorDirective } from '@/_utils/validators/mail-list-validator.directive';
import { MatDatepicker, MatDatepickerModule } from '@angular/material/datepicker';
import { MatSelectModule } from '@angular/material/select';
import { InternalStatusDto } from '../../vindication.model';

export type InlineEditInputType = 'text' | 'tel' | 'textarea' | 'mail' | 'date' | 'select';

@Component({
	selector: 'app-inline-edit',
	standalone: true,
	imports: [
		FormsModule,
		MatButtonModule,
		MatDatepickerModule,
		MatFormFieldModule,
		MatInputModule,
		MatProgressBarModule,
		MatSelectModule,
		MatTooltipModule,
		MailListValidatorDirective,
	],
	providers: [
		{ provide: DateAdapter, useClass: PolishDateAdapter },
		{ provide: MAT_DATE_LOCALE, useValue: 'pl-PL' },
	],
	templateUrl: './inline-edit.component.html',
	styleUrl: './inline-edit.component.scss',
	changeDetection: ChangeDetectionStrategy.OnPush,
})
export class InlineEditComponent {
	readonly value = input.required<string | string[] | number | null>();
	readonly isLoading = input<boolean>(false);
	readonly inputType = input<InlineEditInputType>('text');
	readonly options = input<InternalStatusDto[]>([]);
	readonly tooltip = input<string>('');
	readonly pattern = input<string>('');

	readonly save = output<string | string[] | number | null>();

	readonly isEditing = signal(false);
	readonly editValue = signal<string | number | null>('');

	readonly inputModel = viewChild<NgModel>('inputModel');
	readonly datePickerRef = viewChild<MatDatepicker<Date>>('datePicker');

	readonly isMail = computed(() => this.inputType() === 'mail');
	readonly isDate = computed(() => this.inputType() === 'date');
	readonly isSelect = computed(() => this.inputType() === 'select');

	readonly editDateForPicker = computed(() => {
		const s = this.editValue();
		if (!s) {
			return null;
		}
		const d = new Date(s);
		return Number.isNaN(d.getTime()) ? null : d;
	});

	readonly displayValue = computed(() => {
		const val = this.value();
		if (this.isDate()) {
			return this.formatDateDisplay(val);
		}
		if (this.isSelect()) {
			return this.getSelectLabel(val);
		}
		return this.formatValue(val);
	});

	constructor() {
		effect(
			() => {
				if (!this.isEditing()) {
					this.editValue.set(this.getInitialEditValue());
				}
			},
			{ allowSignalWrites: true },
		);
	}

	startEdit(): void {
		if (this.isLoading()) {
			return;
		}
		this.editValue.set(this.getInitialEditValue());
		this.isEditing.set(true);
		if (this.isDate()) {
			setTimeout(() => this.datePickerRef()?.open(), 0);
		}
	}

	cancelEdit(): void {
		this.isEditing.set(false);
		this.editValue.set(this.getInitialEditValue());
	}

	confirmEdit(): void {
		if (this.isLoading()) {
			return;
		}
		if (this.isDate() || this.isSelect()) {
			this.emitSaveAndClose(this.editValue());
			return;
		}
		const model = this.inputModel();
		if (model?.invalid) {
			this.markInvalid(model);
			return;
		}
		this.emitSaveAndClose(this.getValueToSave());
	}

	setEditDate(date: Date | null): void {
		this.editValue.set(date ? formatDate(date, 'yyyy-MM-dd', 'en') : '');
	}

	onKeydown(event: KeyboardEvent): void {
		if (event.key === 'Enter' && this.inputType() !== 'textarea') {
			this.confirmEdit();
		} else if (event.key === 'Escape') {
			this.cancelEdit();
		}
	}

	private getInitialEditValue(): string | number | null {
		const val = this.value();
		if (this.isSelect()) {
			return this.selectValueToId(val);
		}
		return this.formatValue(val);
	}

	private getSelectLabel(val: string | string[] | number | null): string {
		const id = this.selectValueToId(val);
		return id == null ? '' : (this.options().find((o) => o.id === id)?.name ?? '');
	}

	private selectValueToId(val: string | string[] | number | null): number | null {
		if (val == null || val === '') {
			return null;
		}
		const n = Number(val);
		return Number.isNaN(n) ? null : n;
	}

	private emitSaveAndClose(value: string | string[] | number | null): void {
		this.save.emit(value);
		this.isEditing.set(false);
	}

	private getValueToSave(): string | string[] | number | null {
		const v = this.editValue();
		return this.isMail() ? this.parseMails(String(v ?? '')) : v;
	}

	private formatDateDisplay(val: string | string[] | number | null): string {
		if (val === null || typeof val !== 'string') {
			return '';
		}
		const d = new Date(val);
		return Number.isNaN(d.getTime()) ? '' : formatDate(d, 'dd.MM.yyyy', 'pl-PL');
	}

	private formatValue(val: string | string[] | number | null): string {
		if (val === null) {
			return '';
		}
		if (typeof val === 'number') {
			return String(val);
		}
		if (Array.isArray(val)) {
			return val.join(', ');
		}
		return val;
	}

	private parseMails(value: string | null | undefined): string[] {
		if (!value) {
			return [];
		}
		return value
			.split(',')
			.map((v) => v.trim())
			.filter((v) => v.length > 0);
	}

	private markInvalid(model: NgModel): void {
		model.control?.markAsTouched();
		model.control?.markAsDirty();
		model.control?.updateValueAndValidity();
	}
}
